import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, ViewChildren, QueryList, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { MessageService } from 'primeng/api';

// Local
import { FormDefinition } from '../form-designer/models/form.model';
import { Section, Row } from '../form-designer/models/section.model';
import { Field, FieldType } from '../form-designer/models/field.model';
import { FieldRendererComponent } from './field-renderer/field-renderer.component';
import { buildLookupDropdownOptions, isLookupDropdownSelected } from './repeatable-lookup.util';
import { FormDependencyService } from '../form-designer/services/form-dependency.service';
import { SectionNavigatorComponent } from '../form-designer/section-navigator/section-navigator.component';
import { LayoutService } from '../../../services';
import { AiService } from '../../../services/ai.service';
import { toPTDateString } from '../../../shared/utils';
import { asText } from "../../../shared/coerce";
export interface FormInvalidFieldItem {
  label: string;
  error: string;
}

export interface FormInvalidFieldGroup {
  tabIndex?: number;
  tabName?: string;
  sectionName: string;
  fields: FormInvalidFieldItem[];
}

@Component({
  selector: 'app-form-viewer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    SkeletonModule,
    TabsModule,
    TooltipModule,
    MultiSelectModule,
    FieldRendererComponent,
  ],
  templateUrl: './form-viewer.component.html',
  styleUrls: ['./form-viewer.component.scss'],
})
export class FormViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() loading: boolean = true;
  @Input() mode: 'preview' | 'normal' = 'normal';
  @Input() readonly: boolean = false;
  @Input() initialData?: { [key: string]: any };
  @Input() formState: 'New' | 'Edit' = 'Edit';
  /** When true, saved values (including null/empty) are used as-is; design defaults are not applied. */
  @Input() isSaved: boolean = false;
  @Input() enableSectionCollapse: boolean = true;
  @Input() enableSectionNavigator: boolean = true;
  @Input() enableCopyPaste: boolean = false;

  @Output() valueChange = new EventEmitter<any>();
  @Output() validationChange = new EventEmitter<any>();

  // ViewChildren to reference all field-renderer components
  @ViewChildren(FieldRendererComponent) fieldRenderers!: QueryList<FieldRendererComponent>;

  duplicateInstances: Set<string> = new Set();
  duplicateAddressSections: Set<string> = new Set();


  // FormDefinition input
  @Input() set formDefinition(value: FormDefinition | null) {
    if (value) {
      this._formDefinition = value;
      this.initForm();
    }
  }

  get formDefinition(): FormDefinition | null {
    return this._formDefinition;
  }

  private _formDefinition: FormDefinition | null = null;
  form: FormGroup;
  activeTabIndex: number = 0;

  // Repeatable fields management
  private readonly repeatableFieldArrays: Map<string, FormArray> = new Map();
  private readonly lookupOptionsByField: Record<string, Record<string, unknown>[]> = {};
  private readonly lookupMultiSelectControls = new Map<string, FormControl<string[] | null>>();

  // Complex fields management
  private readonly complexFieldGroups: Map<string, FormGroup> = new Map();

  // Field dependency management
  private readonly destroy$ = new Subject<void>();
  private readonly fieldEnabledStates: Map<string, boolean> = new Map();
  private readonly fieldVisibleStates: Map<string, boolean> = new Map();
  private readonly fieldDefinitionByKey: Map<string, Field> = new Map();
  private isInitializing: boolean = false;

  // Section collapse state management
  sectionExpandedStates: Map<string, boolean> = new Map();

  // Instance loading states for copy operations
  private readonly instanceLoadingStates: Map<string, boolean> = new Map();

  /**
   * Get sections for the current active tab
   */
  get currentTabSections(): Section[] {
    if (!this._formDefinition?.tabs || this._formDefinition.tabs.length === 0) {
      return [];
    }
    const activeTab = this._formDefinition.tabs[this.activeTabIndex];
    return activeTab ? activeTab.sections : [];
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly formDependencyService: FormDependencyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly layoutService: LayoutService,
    private readonly aiService: AiService,
    private readonly messageService: MessageService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    // Initialize section navigator if enabled and form definition exists
    if (this.enableSectionNavigator && this._formDefinition) {
      this.updateSectionNavigator();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly'] && !changes['readonly'].firstChange) {
      if (this.readonly) {
        this.form.disable({ emitEvent: false });
      }
    }

    if (changes['enableSectionNavigator'] && !changes['enableSectionNavigator'].firstChange) {
      if (!this.enableSectionNavigator) {
        this.layoutService.closePanel();
      } else if (this._formDefinition) {
        this.updateSectionNavigator();
      }
    }
    if (this._formDefinition && (changes['initialData'] || changes['isSaved'])) {
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Close the right panel when component is destroyed
    this.layoutService.closePanel();
  }

  private initForm() {
    if (!this._formDefinition?.tabs) return;

    // Set initialization flag to prevent clearing initial values
    this.isInitializing = true;

    this.form = this.fb.group({});
    this.lookupMultiSelectControls.clear();

    // Process all tabs and their sections

    this._formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        this.processSection(section);
      });
    });

    // Extract all fields and update dependency service
    const allFields = this.extractAllFields();
    this.buildFieldDefinitionMap();
    this.formDependencyService.updateFormFields(allFields);

    // Initialize field enabled states
    this.initializeFieldStates(allFields);

    // Setup form value change listener with dependency handling
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.updateFieldStates();
        this.valueChange.emit(value);
      });

    // Setup validation listener
    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.validationChange.emit({
          valid: status === 'VALID',
          errors: this.getFormErrors()
        });
      });

    // Initialize section expanded states (all sections expanded by default)
    this.initializeSectionStates();

    this.loading = false;
    // Initialization complete, allow clearing disabled field values on future changes
    this.isInitializing = false;

    // Initialize section navigator
    this.updateSectionNavigator();
  }

  /**
   * Initialize all sections as expanded by default
   */
  private initializeSectionStates(): void {
    if (!this._formDefinition) return;

    this._formDefinition.tabs.forEach((tab, tabIndex) => {
      tab.sections.forEach((section, sectionIndex) => {
        const sectionId = this.getSectionId(tabIndex, sectionIndex);
        this.sectionExpandedStates.set(sectionId, true); // Default: expanded
      });
    });
  }

  /**
   * Generate unique section ID
   */
  private getSectionId(tabIndex: number, sectionIndex: number): string {
    return `tab-${tabIndex}-section-${sectionIndex}`;
  }

  /**
   * Toggle section expand/collapse state
   */
  toggleSection(tabIndex: number, sectionIndex: number): void {
    const sectionId = this.getSectionId(tabIndex, sectionIndex);
    const currentState = this.sectionExpandedStates.get(sectionId) ?? true;
    this.sectionExpandedStates.set(sectionId, !currentState);
  }

  /**
   * Check if section is expanded
   */
  isSectionExpanded(tabIndex: number, sectionIndex: number): boolean {
    const sectionId = this.getSectionId(tabIndex, sectionIndex);
    return this.sectionExpandedStates.get(sectionId) ?? true;
  }

  /**
   * Extract all fields from the form definition
   * Note: Sub-fields of repeatable fields are NOT included here.
   * They are handled separately via isSubFieldEnabled() method for instance-level dependency checking.
   */
  private extractAllFields(): Field[] {
    const fields: Field[] = [];

    if (!this._formDefinition) return fields;

    this._formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          if (!row.isRepeatableRow) {
            // Collect regular fields
            row.fields.forEach(columnFields => {
              columnFields.forEach(field => {
                fields.push(field);
              });
            });
          } else if (row.repeatableKeywordField) {
            // Include repeatable field itself (but NOT its sub-fields)
            // Sub-fields are accessed via repeatableKeywordField.createdSubFields
            fields.push(row.repeatableKeywordField);
          }
        });
      });
    });

    return fields;
  }

  private buildFieldDefinitionMap(): void {
    this.fieldDefinitionByKey.clear();
    if (!this._formDefinition) return;

    this._formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          if (!row.isRepeatableRow) {
            row.fields.forEach(columnFields => {
              columnFields.forEach(field => {
                this.fieldDefinitionByKey.set(field.key, field);
              });
            });
          } else if (row.repeatableKeywordField) {
            const parentField = row.repeatableKeywordField;
            this.fieldDefinitionByKey.set(parentField.key, parentField);
            const subFields = (parentField as any).createdSubFields as Field[] | undefined;
            subFields?.forEach(subField => {
              this.fieldDefinitionByKey.set(subField.key, subField);
            });
          }
        });
      });
    });
  }

  private findFieldDefinition(fieldKey: string): Field | undefined {
    return this.fieldDefinitionByKey.get(fieldKey);
  }

  /**
   * Initialize field enabled states
   */
  private initializeFieldStates(allFields: Field[]): void {
    allFields.forEach(field => {
      // Initially set all fields to true, updateFieldStates() will correct this based on dependencies
      this.fieldEnabledStates.set(field.key, true);
      this.fieldVisibleStates.set(field.key, true);
    });

    this.updateFieldStates();
  }

  /**
   * Update field enabled states based on current form values
   */
  private updateFieldStates(): void {
    const formValues = this.form.value;

    this.fieldEnabledStates.forEach((_, fieldKey) => {
      const enabled = this.formDependencyService.getFieldEnabledState(fieldKey, formValues);
      const visible = this.formDependencyService.getFieldVisibleState(fieldKey, formValues);
      this.fieldEnabledStates.set(fieldKey, enabled);
      this.fieldVisibleStates.set(fieldKey, visible);

      // Only clear field values if not during initialization and field is disabled
      if (!enabled && !this.isInitializing) {
        const field = this.findFieldDefinition(fieldKey);
        if (field?.disabledBehavior === 'hideAndClear') {
          const control = this.form.get(fieldKey);
          if (control && control.value != null && control.value !== '') {
            control.setValue(null, { emitEvent: false });
          }
        }
      }
    });

    this.clearHiddenSubFieldValues();
    // Manually trigger change detection to ensure OnPush child components update
    this.cdr.markForCheck();
  }

  /**
   * Check if a field is enabled based on its dependencies
   */
  isFieldEnabled(fieldKey: string): boolean {
    return this.fieldEnabledStates.get(fieldKey) ?? true;
  }

  isFieldVisible(fieldKey: string): boolean {
    return this.fieldVisibleStates.get(fieldKey) ?? true;
  }

  /**
   * Check if a sub-field in a repeatable instance is enabled based on its dependencies
   * This method handles instance-level dependencies within repeatable fields
   * @param subField - The sub-field to check
   * @param instanceGroup - The FormGroup for this specific instance
   * @param repeatableField - The parent repeatable field (unused but kept for future extensions)
   */
  isSubFieldEnabled(subField: Field, instanceGroup: FormGroup, repeatableField?: Field): boolean {
    // If field has no dependency, it's always enabled
    if (!subField.dependsOn) {
      return true;
    }

    // Get the dependency field value from the same instance
    const dependencyControl = instanceGroup.get(subField.dependsOn);
    if (!dependencyControl) {
      // Dependency field not found, default to enabled
      return true;
    }

    const dependencyValue = dependencyControl.value;

    // Check if the dependency condition is met
    const enableWhen = subField.enableWhen ?? true;
    return dependencyValue === enableWhen;
  }

  isSubFieldVisible(subField: Field, instanceGroup: FormGroup): boolean {
    if (this.isSubFieldEnabled(subField, instanceGroup)) {
      return true;
    }
    return subField.disabledBehavior !== 'hideAndClear';
  }

  private clearHiddenSubFieldValues(): void {
    if (this.isInitializing || !this._formDefinition) {
      return;
    }

    this._formDefinition.tabs.forEach(tab => {
      tab.sections.forEach(section => {
        section.rows.forEach(row => {
          if (!row.isRepeatableRow || !row.repeatableKeywordField) {
            return;
          }

          const parentField = row.repeatableKeywordField;
          const subFields = ((parentField as any).createdSubFields as Field[] | undefined) ?? [];

          if (row.type === 'Complex') {
            const instanceGroup = this.complexFieldGroups.get(parentField.key);
            if (instanceGroup) {
              subFields.forEach(subField => this.clearSubFieldIfHidden(subField, instanceGroup));
            }
          } else {
            const formArray = this.repeatableFieldArrays.get(parentField.key);
            formArray?.controls.forEach(instanceControl => {
              subFields.forEach(subField =>
                this.clearSubFieldIfHidden(subField, instanceControl as FormGroup)
              );
            });
          }
        });
      });
    });
  }

  private clearSubFieldIfHidden(subField: Field, instanceGroup: FormGroup): void {
    if (this.isSubFieldEnabled(subField, instanceGroup)) {
      return;
    }
    if (subField.disabledBehavior !== 'hideAndClear') {
      return;
    }
    const control = instanceGroup.get(subField.key);
    if (control && control.value != null && control.value !== '') {
      control.setValue(null, { emitEvent: false });
    }
  }

  private convertStringToDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'string') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  /** True when formData has a meaningful value for this field (not null/undefined/blank string). */
  private isFormDataValuePresent(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return true;
  }

  private isTimeField(field: Field): boolean {
    return field.type === FieldType.TIME
      || field['renderType'] === FieldType.TIME
      || field['renderType'] === 'time';
  }

  /**
   * Prefer saved formData; when missing and not yet saved, use defaultValue from form design.
   * Date fields pass through unchanged so the date-picker can display the stored value as-is.
   */
  private resolveControlValue(field: Field, formDataValue: unknown): unknown {
    const source = this.isSaved
      ? (formDataValue !== undefined ? formDataValue : null)
      : (this.isFormDataValuePresent(formDataValue)
        ? formDataValue
        : (field.defaultValue ?? null));

    if (this.isTimeField(field)) {
      return this.convertStringToDate(source);
    }

    return source;
  }

  private processSection(section: Section) {
    section.rows.forEach(row => {
      this.processRow(row);
    });
  }


  private processRow(row: Row) {
    if (row.isRepeatableRow && row.repeatableKeywordField) {
      // Only use Complex type when explicitly set to 'Complex'
      // Otherwise default to Repeatable (for backward compatibility)
      if (row.type === 'Complex') {
        this.addComplexFieldControl(row.repeatableKeywordField);
      } else {
        // Default to Repeatable type (including when type is undefined/null)
        this.addRepeatableFieldControl(row.repeatableKeywordField);
      }
    } else {
      row.fields.forEach(columnFields => {
        columnFields.forEach(field => {
          this.addFieldControl(field);
        });
      });
    }
  }

  private addFieldControl(field: Field) {
    const resolvedValue = this.resolveControlValue(field, this.initialData?.[field.key]);
    const validators = this.getFieldValidators(field);

    const control = this.fb.control({
      value: resolvedValue,
      disabled: this.readonly || field.readOnly
    }, validators);

    this.form.addControl(field.key, control);
  }

  private getFieldValidators(field: Field): any[] {
    const validators = [];

    if (field.readOnly) {
      return [];
    }

    if (field.required) {
      validators.push(Validators.required);
    }

    // Add more validators based on field type
    switch (field.type) {
      case FieldType.NUMBER:
        if (field['min'] !== undefined) {
          validators.push(Validators.min(field['min']));
        }
        if (field['max'] !== undefined) {
          validators.push(Validators.max(field['max']));
        }
        break;
      case FieldType.TEXT:
        if (field['maxLength'] !== undefined) {
          validators.push(Validators.maxLength(field['maxLength']));
        }
        if (field['minLength'] !== undefined) {
          validators.push(Validators.minLength(field['minLength']));
        }
        break;
    }

    return validators;
  }

  private getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control?.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  getDropdownOptions(options: any[] = []): any[] {
    if (!options || options.length === 0) {
      return [];
    }

    // Handle keyword field options format: { key: string; value: string }[]
    if (options[0] && typeof options[0] === 'object' && 'key' in options[0] && 'value' in options[0]) {
      return options.map(opt => ({ label: opt.value, value: opt.value }));
    }

    // Handle simple string array format
    return options.map(opt => ({ label: opt, value: opt }));
  }

  isFieldInvalid(fieldKey: string): boolean {
    const control = this.form.get(fieldKey);
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  getFieldError(fieldKey: string): string | null {
    const control = this.form.get(fieldKey);
    if (control?.errors && (control.dirty || control.touched)) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['minlength']) {
        return `Minimum length is ${control.errors['minlength'].requiredLength}`;
      }
      if (control.errors['maxlength']) {
        return `Maximum length is ${control.errors['maxlength'].requiredLength}`;
      }
      if (control.errors['min']) {
        return `Minimum value is ${control.errors['min'].min}`;
      }
      if (control.errors['max']) {
        return `Maximum value is ${control.errors['max'].max}`;
      }
      if (control.errors['pattern']) {
        return 'Invalid format';
      }
    }
    return null;
  }

  // Keyword field display type logic - uses renderType from KeywordFieldStrategy
  getKeywordFieldDisplayType(field: Field): string {
    return field.type !== FieldType.KEYWORD_FIELD ? field.type : field['renderType'];
  }

  getEffectiveFieldType(field: Field): string {
    return field.type === FieldType.KEYWORD_FIELD ? this.getKeywordFieldDisplayType(field) : field.type;
  }

  getMaxLength(field: Field, fieldType: string): number | null {
    if (fieldType === FieldType.TEXT) {
      // Single line text: use field value if exists, otherwise default to 255
      return field['maxLength'] ?? 255;
    } else if (fieldType === FieldType.TEXTAREA) {
      // Multi-line text: use field value if exists, otherwise no limit
      return field['maxLength'] || null;
    }
    return null;
  }

  // Complex Field Methods

  private addComplexFieldControl(field: Field) {
    const fieldKey = field.key;
    const initialData = this.initialData?.[fieldKey] || {};

    const formGroup = this.createComplexInstanceGroup(field, initialData);

    this.form.addControl(fieldKey, formGroup);
    this.complexFieldGroups.set(fieldKey, formGroup);
  }

  private createComplexInstanceGroup(
    complexField: Field,
    instanceData: any = {}
  ): FormGroup {
    const group: any = {};

    const subFields = this.getSubFields(complexField);
    subFields.forEach(subField => {
      const resolvedValue = this.resolveControlValue(subField, instanceData[subField.key]);
      const validators = this.getFieldValidators(subField);

      const isReadOnly = this.readonly || complexField.readOnly || subField.readOnly;

      group[subField.key] = this.fb.control({
        value: resolvedValue,
        disabled: isReadOnly
      }, validators);
    });

    // Handle undefined fields that exist in instanceData but not in form definition
    Object.keys(instanceData).forEach(key => {
      if (!group[key]) {
        // Create simple FormControl for undefined fields (pure data storage)
        group[key] = this.fb.control(instanceData[key]);
      }
    });

    return this.fb.group(group);
  }

  getComplexInstance(fieldKey: string): FormGroup {
    return this.complexFieldGroups.get(fieldKey) || this.fb.group({});
  }

  copyComplexInstance(fieldKey: string): void {
    const complexField = this.findFieldByKey(fieldKey);
    if (!complexField) {
      console.error('Complex field not found:', fieldKey);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Field configuration not found'
      });
      return;
    }

    const instanceGroup = this.getComplexInstance(fieldKey);
    const subFields = this.getSubFields(complexField);

    const { data, hasData } = this.extractInstanceData(subFields, instanceGroup);

    if (!hasData) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No data to copy'
      });
      return;
    }

    const jsonString = JSON.stringify(data);

    this.setComplexInstanceLoading(fieldKey, true);

    this.copyFormattedDataToClipboard(
      jsonString,
      (loading) => this.setComplexInstanceLoading(fieldKey, loading),
      'Entry copied to clipboard'
    );
  }

  private getComplexInstanceLoadingKey(fieldKey: string): string {
    return `complex_${fieldKey}`;
  }

  isComplexInstanceLoading(fieldKey: string): boolean {
    const key = this.getComplexInstanceLoadingKey(fieldKey);
    return this.instanceLoadingStates.get(key) ?? false;
  }

  private setComplexInstanceLoading(fieldKey: string, loading: boolean): void {
    const key = this.getComplexInstanceLoadingKey(fieldKey);
    this.instanceLoadingStates.set(key, loading);
    this.cdr.markForCheck();
  }

  getComplexFieldReadonlyState(subField: Field, complexField: Field): boolean {
    return this.readonly || complexField.readOnly || subField.readOnly;
  }

  isComplexSubFieldEnabled(subField: Field, fieldKey: string): boolean {
    if (!subField.dependsOn) {
      return true;
    }
    const instanceGroup = this.getComplexInstance(fieldKey);
    const dependencyControl = instanceGroup.get(subField.dependsOn);

    if (!dependencyControl) {
      return true;
    }

    const dependencyValue = dependencyControl.value;
    const enableWhen = subField.enableWhen ?? true;

    return dependencyValue === enableWhen;
  }

  isComplexSubFieldVisible(subField: Field, fieldKey: string): boolean {
    if (this.isComplexSubFieldEnabled(subField, fieldKey)) {
      return true;
    }
    return subField.disabledBehavior !== 'hideAndClear';
  }


  // Repeatable Field Methods

  private addRepeatableFieldControl(field: Field) {
    const fieldKey = field.key;
    const initialData = this.initialData?.[fieldKey] || [];
    const isDropdown = this.isLookupDropdown(field);

    if (isDropdown && Array.isArray(initialData) && initialData.length) {
      this.lookupOptionsByField[fieldKey] = initialData;
    }

    const selectionKey = field['selectionFieldKey'] as string | undefined;
    const instancesData = isDropdown
      ? (initialData as any[]).filter(i => isLookupDropdownSelected(i, selectionKey))
      : (initialData.length > 0 ? initialData : []);

    const formArray = this.fb.array(
      instancesData.map((instanceData: any, index: number) =>
        this.createRepeatableInstanceGroup(field, instanceData, index < instancesData.length)
      )
    );

    this.form.addControl(fieldKey, formArray);
    this.repeatableFieldArrays.set(fieldKey, formArray);

    if (isDropdown) {
      const selectedIds = instancesData
        .map((i: Record<string, unknown>) => i['RowId'])
        .filter((id: unknown) => id != null && id !== '')
        .map(String);
      this.lookupMultiSelectControls.set(
        fieldKey,
        this.fb.control<string[] | null>({ value: selectedIds, disabled: this.readonly || field.readOnly })
      );
    }
  }

  getLookupMultiSelectControl(field: Field): FormControl<string[] | null> {
    return this.lookupMultiSelectControls.get(field.key) ?? this.fb.control<string[] | null>([]);
  }

  isLookupDropdown(field: Field): boolean {
    return field['enableItemSelection'] === true && field['isDropdown'] === true;
  }

  getLookupDropdownOptions(field: Field): { label: string; value: string }[] {
    return buildLookupDropdownOptions(
      this.lookupOptionsByField[field.key] ?? [],
      field['combinationDisplayNames'] ?? [],
      field['subFields'] ?? []
    );
  }

  onLookupMultiSelectChange(field: Field, selectedRowIds: string[] | null): void {
    const ids = selectedRowIds ?? [];
    const maxCount = this.getMaxRepeatCount(field);
    const trimmedIds = ids.length > maxCount ? ids.slice(0, maxCount) : ids;

    if (ids.length > maxCount) {
      this.getLookupMultiSelectControl(field).setValue(trimmedIds, { emitEvent: false });
    }

    const formArray = this.repeatableFieldArrays.get(field.key);
    if (!formArray) return;

    const records = this.lookupOptionsByField[field.key] ?? [];
    const selKey = field['selectionFieldKey'] as string | undefined;
    const existingIds = new Set(
      formArray.controls.map(g => asText(g.get('RowId')?.value ?? '')).filter(Boolean)
    );

    for (let i = formArray.length - 1; i >= 0; i--) {
      const rowId = asText(formArray.at(i).get('RowId')?.value ?? '');
      if (rowId && !trimmedIds.includes(rowId)) {
        formArray.removeAt(i);
      }
    }

    for (const rowId of trimmedIds) {
      if (existingIds.has(rowId)) continue;
      const record = records.find(r => asText(r['RowId']) === rowId);
      if (record) {
        formArray.push(this.createRepeatableInstanceGroup(field, record, false));
      }
    }

    formArray.controls.forEach(group => {
      if (selKey) group.get(selKey)?.setValue(true, { emitEvent: false });
    });

    this.cdr.markForCheck();
  }

  private createRepeatableInstanceGroup(
    repeatableField: Field,
    instanceData: any = {},
    isFromInitialData: boolean = false
  ): FormGroup {
    const group: any = {};

    // Handle selection field if enableItemSelection is true
    if (repeatableField['enableItemSelection'] === true) {
      const selectionFieldKey = repeatableField['selectionFieldKey'];
      const isDropdown = this.isLookupDropdown(repeatableField);
      if (selectionFieldKey) {
        const selectionValue = isDropdown
          ? (this.isFormDataValuePresent(instanceData[selectionFieldKey])
            ? instanceData[selectionFieldKey]
            : false)
          : (this.isSaved
            ? (instanceData[selectionFieldKey] !== undefined ? instanceData[selectionFieldKey] : null)
            : (this.isFormDataValuePresent(instanceData[selectionFieldKey])
              ? instanceData[selectionFieldKey]
              : (repeatableField['defaultValue'] ?? true)));
        group[selectionFieldKey] = this.fb.control({
          value: selectionValue,
          disabled: this.readonly
        });
      }

      if (isDropdown) {
        group['RowId'] = this.fb.control({
          value: instanceData['RowId'] ?? null,
          disabled: this.readonly
        });
      }
    }

    const subFields = this.getSubFields(repeatableField);
    subFields.forEach(subField => {
      const resolvedValue = this.resolveControlValue(subField, instanceData[subField.key]);
      const validators = this.getFieldValidators(subField);

      // New readonly logic: consider createOnly for instances from initialData
      const isReadOnly = this.readonly ||
        repeatableField.readOnly ||
        subField.readOnly ||
        (isFromInitialData && subField['createOnly'] === true) ||
        this.isLookupDropdown(repeatableField);

      group[subField.key] = this.fb.control({
        value: resolvedValue,
        disabled: isReadOnly
      }, validators);
    });

    // Handle undefined fields that exist in instanceData but not in form definition
    Object.keys(instanceData).forEach(key => {
      if (!group[key]) {
        // Create simple FormControl for undefined fields (pure data storage)
        group[key] = this.fb.control(instanceData[key]);
      }
    });

    // Add metadata to track data source
    const formGroup = this.fb.group(group);
    (formGroup as any)._isFromInitialData = isFromInitialData;

    return formGroup;
  }

  addRepeatableInstance(field: Field) {
    const formArray = this.repeatableFieldArrays.get(field.key);
    if (formArray && formArray.length < this.getMaxRepeatCount(field)) {
      // New instance, mark as not from initial data
      const newInstanceGroup = this.createRepeatableInstanceGroup(field, {}, false);
      formArray.push(newInstanceGroup);
    }
  }

  removeRepeatableInstance(fieldKey: string, instanceIndex: number) {
    const formArray = this.repeatableFieldArrays.get(fieldKey);
    if (formArray) {
      formArray.removeAt(instanceIndex);
    }
  }

  getSubFields(field: Field): Field[] {
    const subFields = (field as any).createdSubFields || [];
    // Filter out sub-fields where hidden === true
    return subFields.filter((subField: Field) => subField['hidden'] !== true);
  }

  getRepeatableInstances(fieldKey: string): FormGroup[] {
    const formArray = this.repeatableFieldArrays.get(fieldKey);
    return formArray ? formArray.controls as FormGroup[] : [];
  }

  getRepeatableInstanceCount(fieldKey: string): number {
    return this.getRepeatableInstances(fieldKey).length;
  }

  getMaxRepeatCount(field: Field): number {
    return (field as any).maxRepeatCount || 10;
  }

  getColumnLayout(field: Field): number {
    return (field as any).columnLayout || 2;
  }

  /**
   * Check if a repeatable instance can be removed
   * Instance cannot be removed if:
   * 1. It comes from initialData (isFromInitialData = true)
   * 2. It has a non-empty RowId
   * 3. The repeatable field contains any subField with createOnly = true
   */
  canRemoveInstance(repeatableField: Field, instanceGroup: FormGroup, instanceIndex: number): boolean {
    // Check if instance comes from initial data
    const isFromInitialData = (instanceGroup as any)._isFromInitialData;
    if (!isFromInitialData) {
      return true; // New instances can always be removed
    }

    // Check if RowId exists and is not empty
    const rowIdControl = instanceGroup.get('RowId');
    const hasRowId = rowIdControl?.value;
    if (!hasRowId) {
      return true; // Instances without RowId can be removed
    }

    // Check if any subField has createOnly = true
    const subFields = this.getSubFields(repeatableField);
    const hasCreateOnlyField = subFields.some(subField => subField['createOnly'] === true);

    // Cannot remove if it has createOnly fields
    return !hasCreateOnlyField;
  }

  /**
   * Determine if a subfield should be readonly based on various conditions
   */
  getSubFieldReadonlyState(subField: Field, repeatableField: Field, instanceGroup: FormGroup): boolean {
    const globalReadonly = this.readonly;
    const fieldReadonly = repeatableField.readOnly || subField.readOnly;
    const isFromInitialData = (instanceGroup as any)._isFromInitialData;
    // Hard code RowId check because RowID equals non-null means existing record in database, otherwise it's a draft record
    const createOnlyReadonly = isFromInitialData && (!(instanceGroup.value['RowId'] == null)) && subField['createOnly'] === true;
    const lookupDropdownReadonly = this.isLookupDropdown(repeatableField);

    return globalReadonly || fieldReadonly || createOnlyReadonly || lookupDropdownReadonly;
  }

  /**
   * Check if copy/paste functionality should be shown for a repeatable field
   * Returns true if at least one sub-field has includeInCopyPaste = true
   */
  shouldShowCopyPaste(repeatableField: Field): boolean {
    const subFields = this.getSubFields(repeatableField);

    // If no sub-fields, don't show copy/paste
    if (subFields.length === 0) {
      return false;
    }

    // Check if at least one sub-field has includeInCopyPaste = true
    // By default, includeInCopyPaste is true if not explicitly set to false
    return subFields.some(subField => subField['includeInCopyPaste'] !== false);
  }

  /**
   * Get unique key for instance loading state
   */
  private getInstanceLoadingKey(fieldKey: string, instanceIndex: number): string {
    return `${fieldKey}_${instanceIndex}`;
  }

  /**
   * Check if instance is in loading state
   */
  isInstanceLoading(fieldKey: string, instanceIndex: number): boolean {
    const key = this.getInstanceLoadingKey(fieldKey, instanceIndex);
    return this.instanceLoadingStates.get(key) ?? false;
  }

  /**
   * Set instance loading state
   */
  private setInstanceLoading(fieldKey: string, instanceIndex: number, loading: boolean): void {
    const key = this.getInstanceLoadingKey(fieldKey, instanceIndex);
    this.instanceLoadingStates.set(key, loading);
    this.cdr.markForCheck(); // Trigger change detection for OnPush
  }

  /**
   * Get unique key for all instances loading state
   */
  private getAllInstancesLoadingKey(fieldKey: string): string {
    return `${fieldKey}_all`;
  }

  /**
   * Check if all instances are in loading state
   */
  isAllInstancesLoading(fieldKey: string): boolean {
    const key = this.getAllInstancesLoadingKey(fieldKey);
    return this.instanceLoadingStates.get(key) ?? false;
  }

  /**
   * Set all instances loading state
   */
  private setAllInstancesLoading(fieldKey: string, loading: boolean): void {
    const key = this.getAllInstancesLoadingKey(fieldKey);
    this.instanceLoadingStates.set(key, loading);
    this.cdr.markForCheck();
  }

  /**
   * Copy a single repeatable instance
   */
  copyRepeatableInstance(fieldKey: string, instanceIndex: number): void {
    // 1. Get the repeatable field definition
    const repeatableField = this.findFieldByKey(fieldKey);
    if (!repeatableField) {
      console.error('Repeatable field not found:', fieldKey);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Field configuration not found'
      });
      return;
    }

    // 2. Get the instance FormGroup
    const instances = this.getRepeatableInstances(fieldKey);
    if (instanceIndex < 0 || instanceIndex >= instances.length) {
      console.error('Invalid instance index:', instanceIndex);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid instance index'
      });
      return;
    }
    const instanceGroup = instances[instanceIndex];

    // 3. Get all sub-fields
    const subFields = this.getSubFields(repeatableField);

    // 4. Extract data
    const { data, hasData } = this.extractInstanceData(subFields, instanceGroup);

    if (!hasData) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No data to copy'
      });
      return;
    }

    const jsonString = JSON.stringify(data);

    // 5. Set loading state
    this.setInstanceLoading(fieldKey, instanceIndex, true);

    // 6. Call helper to process
    this.copyFormattedDataToClipboard(
      jsonString,
      (loading) => this.setInstanceLoading(fieldKey, instanceIndex, loading),
      'Entry copied to clipboard'
    );
  }

  /**
   * Copy all repeatable instances
   */
  copyAllRepeatableInstances(fieldKey: string): void {
    // 1. Get the repeatable field definition
    const repeatableField = this.findFieldByKey(fieldKey);
    if (!repeatableField) {
      console.error('Repeatable field not found:', fieldKey);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Field configuration not found'
      });
      return;
    }

    // 2. Get all instance FormGroups
    const instances = this.getRepeatableInstances(fieldKey);
    if (instances.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No entries to copy'
      });
      return;
    }

    // 3. Get all sub-fields
    const subFields = this.getSubFields(repeatableField);

    // 4. Build array of JSON objects
    const allData: any[] = [];

    instances.forEach(instanceGroup => {
      const { data, hasData } = this.extractInstanceData(subFields, instanceGroup);
      if (hasData) {
        allData.push(data);
      }
    });

    if (allData.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No data found to copy'
      });
      return;
    }

    const jsonString = JSON.stringify(allData);

    // 5. Set loading state
    this.setAllInstancesLoading(fieldKey, true);

    // 6. Call helper to process
    this.copyFormattedDataToClipboard(
      jsonString,
      (loading) => this.setAllInstancesLoading(fieldKey, loading),
      'All entries copied to clipboard'
    );
  }

  /**
   * Extract and format data from a repeatable instance group
   */
  private extractInstanceData(subFields: Field[], instanceGroup: FormGroup): { data: { [key: string]: any }, hasData: boolean } {
    const extractedData: { [key: string]: any } = {};
    let hasData = false;

    subFields.forEach(subField => {
      // Check includeInCopyPaste property (default is true if not set)
      const includeInCopyPaste = subField['includeInCopyPaste'] !== false;

      if (includeInCopyPaste) {
        const control = instanceGroup.get(subField.key);
        if (control) {
          let value = control.value;

          const renderType = subField['renderType'] || subField.type;

          // Handle choice fields - convert ID to display text
          const isChoiceField = renderType === FieldType.DROPDOWN ||
            renderType === FieldType.SINGLE_CHOICE ||
            renderType === FieldType.MULTIPLE_CHOICE;

          if (isChoiceField && subField['options'] && subField['options'].length > 0) {
            const options = subField['options'];

            // Check if options are in keyword format (with key and value)
            const isKeywordFormat = options[0] && typeof options[0] === 'object' &&
              'key' in options[0] && 'value' in options[0];

            if (isKeywordFormat) {
              if (Array.isArray(value)) {
                // Multiple choice - array of IDs, convert to array of texts
                value = value.map((v: any) => {
                  const option = options.find((opt: any) => opt.key === v);
                  return option ? option.value : v;
                });
              } else if (value !== null && value !== undefined) {
                // Single choice - single ID, convert to text
                const option = options.find((opt: any) => opt.key === value);
                value = option ? option.value : value;
              }
            }
            // If not keyword format (simple string array), value is already text
          }

          // Handle date fields - format to MM/DD/YYYY
          if (value && (renderType === FieldType.DATE || renderType === 'date')) {
            value = toPTDateString(value);
          }

          // Handle boolean fields - convert to Yes/No
          if (renderType === FieldType.YES_NO || renderType === 'yes_no') {
            if (value === true) {
              value = 'Yes';
            } else if (value === false) {
              value = 'No';
            }
            // If null/undefined, keep as is (empty)
          }

          // Use displayName as key if available, otherwise fall back to field key
          const outputKey = subField['displayName'] || subField.label || subField.key;
          extractedData[outputKey] = value;

          // Check if value is not empty
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              if (value.length > 0) hasData = true;
            } else {
              hasData = true;
            }
          }
        }
      }
    });

    return { data: extractedData, hasData };
  }

  private copyFormattedDataToClipboard(
    jsonString: string,
    setLoading: (loading: boolean) => void,
    successMessage: string
  ): void {
    // Call AI service
    this.aiService.formatRepeatingListsValues(jsonString).subscribe({
      next: (res) => {
        // Copy to clipboard using native API
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(res.formattedText)
            .then(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: successMessage
              });
            })
            .catch((err) => {
              console.error('Failed to copy to clipboard:', err);
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Content formatted but copy failed. Please copy manually.'
              });
            });
        } else {
          // Fallback for older browsers
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Clipboard API not supported. Check console for formatted text.'
          });
        }
      },
      error: (err) => {
        console.error('AI service error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to format entry. Please try again.'
        });
      },
      complete: () => {
        setLoading(false);
      }
    });
  }

  /**
   * Check if the form has been modified from its initial state
   * @returns true if form has been modified, false otherwise
   */
  isFormDirty(): boolean {
    return this.form.dirty;
  }

  /**
   * Get current form values and validation state

   *
   * @returns An object containing:
   *   - valid: boolean indicating if the form passes all validation rules
   *   - data: object containing all current form field values
   *
   * @example
   * ```typescript
   * // Get form data and validate in parent component
   * @ViewChild(FormViewerComponent) formViewer!: FormViewerComponent;
   *
   * onSave() {
   *   const result = this.formViewer.getFormValue();
   *   if (result.valid) {
   *     this.saveData(result.data);
   *   } else {
   *   }
   * }
   *
   * onValidate() {
   *   const { valid, data } = this.formViewer.getFormValue();
   *   this.handleValidation(valid, data);
   * }
   * ```
   *
   * @see isFormDirty() - Use this to check if form has been modified
   * @see resetFormValue() - Use this to reset form to initial state
   */
  getFormValue(skipFocus = false): { valid: boolean, data: any } {
    this.form.markAllAsTouched();

    // Refresh validation state for all field renderers to trigger error message display
    // This is necessary because OnPush change detection strategy doesn't automatically
    // detect the change when markAllAsTouched() is called programmatically
    if (this.fieldRenderers) {
      this.fieldRenderers.forEach(fieldRenderer => {
        fieldRenderer.refreshValidationState();
      });
    }

    // Focus first invalid field if form is invalid
    if (!this.form.valid && !skipFocus) {
      // Use setTimeout to ensure DOM has updated after markAllAsTouched
      setTimeout(() => {
        this.focusFirstInvalidField();
      }, 0);
    }

    // Get raw form value
    // use getRawValue() to include disabled fields
    const formValue = this.form.getRawValue();


    // Clean up empty repeatable field instances
    const cleanedData = this.cleanEmptyRepeatableInstances(formValue);

    return {
      valid: this.form.valid,
      data: cleanedData
    };
  }

  /**
   * Human-readable invalid fields grouped by questionnaire section (call after `getFormValue()` when invalid).
   */
  getInvalidFieldSummary(): FormInvalidFieldGroup[] {
    if (!this._formDefinition?.tabs?.length) {
      return [];
    }

    const groups: FormInvalidFieldGroup[] = [];
    const seen = new Set<string>();
    const showTabNames = this._formDefinition.tabs.length > 1;

    const addInvalid = (
      tabIndex: number,
      tabName: string | undefined,
      sectionName: string,
      label: string,
      control: AbstractControl | null | undefined
    ): void => {
      if (!control || control.disabled || control.valid) {
        return;
      }
      const trimmedLabel = label.trim() || 'Unnamed field';
      const dedupeKey = `${tabIndex}|${tabName ?? ''}|${sectionName}|${trimmedLabel}|${this.describeControlErrors(control)}`;
      if (seen.has(dedupeKey)) {
        return;
      }
      seen.add(dedupeKey);

      let group = groups.find(
        g => g.tabIndex === tabIndex && g.sectionName === sectionName && (showTabNames ? g.tabName === tabName : true)
      );
      if (!group) {
        group = showTabNames && tabName
          ? { tabIndex, tabName, sectionName, fields: [] }
          : { tabIndex, sectionName, fields: [] };
        groups.push(group);
      }
      group.fields.push({
        label: trimmedLabel,
        error: this.describeControlErrors(control),
      });
    };

    const fieldLabel = (field: Field): string =>
      (field.label || field['displayName'] || field.key || '').trim();

    this._formDefinition.tabs.forEach((tab, tabIndex) => {
      const tabName = (tab.title || 'Tab').trim();
      tab.sections.forEach(section => {
        const sectionName = (section.title || 'Section').trim();

        section.rows.forEach(row => {
          if (!row.isRepeatableRow) {
            row.fields.forEach(columnFields => {
              columnFields.forEach(field => {
                if (field.type === FieldType.INSTRUCTION) {
                  return;
                }
                if (!this.isFieldEnabled(field.key)) {
                  return;
                }
                if (!this.isFieldVisible(field.key)) {
                  return;
                }
                addInvalid(
                  tabIndex,
                  showTabNames ? tabName : undefined,
                  sectionName,
                  fieldLabel(field),
                  this.form.get(field.key)
                );
              });
            });
            return;
          }

          const parentField = row.repeatableKeywordField;
          if (!parentField) {
            return;
          }

          if (row.type === 'Complex') {
            const instanceGroup = this.complexFieldGroups.get(parentField.key);
            if (!instanceGroup) {
              return;
            }
            this.getSubFields(parentField).forEach(subField => {
              if (subField.type === FieldType.INSTRUCTION) {
                return;
              }
              if (!this.isComplexSubFieldEnabled(subField, parentField.key)) {
                return;
              }
              if (!this.isComplexSubFieldVisible(subField, parentField.key)) {
                return;
              }
              const label = `${fieldLabel(parentField)} — ${fieldLabel(subField)}`;
              addInvalid(
                tabIndex,
                showTabNames ? tabName : undefined,
                sectionName,
                label,
                instanceGroup.get(subField.key)
              );
            });
            return;
          }

          const formArray = this.repeatableFieldArrays.get(parentField.key);
          const instances = formArray ? (formArray.controls as FormGroup[]) : [];
          instances.forEach((instanceGroup, index) => {
            this.getSubFields(parentField).forEach(subField => {
              if (subField.type === FieldType.INSTRUCTION) {
                return;
              }
              if (!this.isSubFieldEnabled(subField, instanceGroup, parentField)) {
                return;
              }
              if (!this.isSubFieldVisible(subField, instanceGroup)) {
                return;
              }
              const prefix =
                instances.length > 1
                  ? `${fieldLabel(parentField)} (${index + 1}) — `
                  : `${fieldLabel(parentField)} — `;
              addInvalid(
                tabIndex,
                showTabNames ? tabName : undefined,
                sectionName,
                `${prefix}${fieldLabel(subField)}`,
                instanceGroup.get(subField.key)
              );
            });
          });
        });
      });
    });

    return groups;
  }

  /** True when at least one invalid field belongs to the form tab the user is viewing. */
  hasInvalidFieldsOnCurrentTab(): boolean {
    return this.getInvalidFieldSummary().some(
      group => group.tabIndex === this.activeTabIndex && group.fields.length > 0
    );
  }

  /** Invalid fields on form tabs other than the one currently active. */
  getInvalidFieldSummaryForOtherTabs(): FormInvalidFieldGroup[] {
    return this.getInvalidFieldSummary().filter(
      group => group.tabIndex !== this.activeTabIndex && group.fields.length > 0
    );
  }

  private describeControlErrors(control: AbstractControl): string {
    const errors = control.errors;
    if (!errors) {
      return 'Invalid';
    }
    if (errors['required']) {
      return 'This field is required';
    }
    if (errors['minlength']) {
      return `Minimum length is ${errors['minlength'].requiredLength}`;
    }
    if (errors['maxlength']) {
      return `Maximum length is ${errors['maxlength'].requiredLength}`;
    }
    if (errors['min']) {
      return `Minimum value is ${errors['min'].min}`;
    }
    if (errors['max']) {
      return `Maximum value is ${errors['max'].max}`;
    }
    if (errors['pattern']) {
      return 'Invalid format';
    }
    return 'Invalid';
  }

  /**
   * Clean empty instances from repeatable fields
   * Removes instances where all visible field values are empty (null, undefined, empty string, or empty array)
   * This is a temporary solution to handle user intent: clearing all fields = deleting the instance
   * Hidden fields (e.g., Id, RowId) are ignored when determining if instance is empty
   */
  private cleanEmptyRepeatableInstances(data: any): any {
    const cleanedData = { ...data };

    // Iterate through all repeatable field arrays
    this.repeatableFieldArrays.forEach((formArray, fieldKey) => {
      if (Array.isArray(cleanedData[fieldKey])) {
        const repeatableField = this.findFieldByKey(fieldKey);

        // Filter out instances where all visible values are empty
        cleanedData[fieldKey] = cleanedData[fieldKey].filter((instance: any) => {
          if (repeatableField && this.isLookupDropdown(repeatableField)) {
            return isLookupDropdownSelected(
              instance,
              repeatableField['selectionFieldKey'] as string | undefined
            );
          }
          return !this.isInstanceEmpty(instance, fieldKey);
        });

        // Keep as empty array if all instances were filtered out
        // (Don't remove the field key entirely)
      }
    });

    return cleanedData;
  }

  /**
   * Check if a repeatable field instance has all empty values for visible fields
   * Only checks fields defined in form definition (ignores hidden fields like Id, RowId)
   * Returns true if all visible field values are null, undefined, empty string, or empty array
   */
  private isInstanceEmpty(instance: any, fieldKey: string): boolean {
    if (!instance || typeof instance !== 'object') {
      return true;
    }

    // Get the repeatable field definition
    const repeatableField = this.findFieldByKey(fieldKey);
    if (!repeatableField) {
      // Fallback to checking all values if field definition not found
      const values = Object.values(instance);
      if (values.length === 0) return true;
      return values.every(value => this.isValueEmpty(value));
    }

    // Get visible sub-fields (those displayed to user)
    const visibleSubFields = this.getSubFields(repeatableField);

    // If no visible fields, consider it empty
    if (visibleSubFields.length === 0) {
      return true;
    }

    // Check if ALL visible fields are empty
    return visibleSubFields.every(subField => {
      const value = instance[subField.key];
      return this.isValueEmpty(value);
    });
  }

  /**
   * Check if a single value is considered empty
   */
  private isValueEmpty(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  }

  /**
   * Switch to a form tab and scroll to the first invalid field on that tab.
   */
  navigateToTab(tabIndex: number): void {
    this.activeTabIndex = tabIndex;
    this.updateSectionNavigator();
    setTimeout(() => this.focusFirstInvalidField(tabIndex), 100);
  }

  /**
   * Focus on the first invalid field in the form
   * This improves user experience by directing attention to validation errors
   */
  focusFirstInvalidField(tabIndex?: number): void {
    const invalidSelector =
      '.field-container input.ng-invalid, ' +
      '.field-container textarea.ng-invalid, ' +
      '.field-container .ng-invalid input, ' +
      '.field-container p-select.ng-invalid ';

    let invalidControl: HTMLElement | null = null;
    if (tabIndex !== undefined) {
      const tabRoot = document.querySelector(`[data-form-tab-index="${tabIndex}"]`);
      invalidControl = (tabRoot?.querySelector(invalidSelector) as HTMLElement | null) ?? null;
    } else {
      invalidControl = document.querySelector(invalidSelector) as HTMLElement | null;
    }

    if (invalidControl) {
      if (invalidControl.tagName.toLowerCase() === 'p-select') {
        // For PrimeNG dropdown
        const dropdownInput = invalidControl.querySelector('.p-select-dropdown') as HTMLElement;
        if (dropdownInput) {
          dropdownInput.click(); // Open dropdown
          dropdownInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      else {
        invalidControl.focus();
      }
      // Scroll into view with smooth behavior for better UX
      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Mark the form as pristine (not modified) without changing form values

   * @example
   * ```typescript
   * // After successful form submission
   * @ViewChild(FormViewerComponent) formViewer!: FormViewerComponent;
   *
   * async onSubmit() {
   *   const result = this.formViewer.getFormValue();
   *   if (result.valid) {
   *     try {
   *       await this.saveData(result.data);
   *       // Mark form as saved (not dirty) but keep current values
   *       this.formViewer.markFormAsPristine();
   *       // Now isFormDirty() will return false
   *     } catch (error) {
   *       // Handle submission error
   *     }
   *   }
   * }
   * ```
   *
   * @see isFormDirty() - Use this to check if form has been modified
   * @see getFormValue() - Use this to get current form data and validation state
   * @see resetFormValue() - Use this to completely reset form to initial state
   */
  markFormAsPristine(): void {
    this.form.markAsPristine();
  }

  /**
   * Reset the form to its initial state
   *
   * @example
   * ```typescript
   * // Reset form in parent component via ViewChild
   * @ViewChild(FormViewerComponent) formViewer!: FormViewerComponent;
   *
   * onCancel() {
   *   this.formViewer.resetFormValue();
   * }
   *
   * onNewRecord() {
   *   this.formViewer.resetFormValue();
   *   // Form is now back to initial state
   * }
   * ```
   *
   * @see isFormDirty() - Use this method to check if reset is needed
   * @see getFormValue() - Use this to get current form data and validation state
   * @see markFormAsPristine() - Use this to mark form as saved without losing data
   */
  resetFormValue(): void {
    if (this._formDefinition) {
      this.initForm();
    } else {
      this.form.reset();
      if (this.initialData) {
        this.form.patchValue(this.initialData);
      }
    }

    // Manually trigger validation state refresh for all field renderers
    // This is necessary because OnPush change detection strategy doesn't automatically
    // detect the change when form.reset() is called, and we need to ensure all
    // validation error messages are cleared from the UI
    if (this.fieldRenderers) {
      this.fieldRenderers.forEach(fieldRenderer => {
        fieldRenderer.refreshValidationState();
      });
    }
  }

  /**
   * Update the entire form with new data by reconstructing form controls
   * This method allows updating form values with fresh data from backend
   *
   * @param newData - New data object to populate the form
   * @param updateInitialData - Whether to update the initialData reference (default: true)
   *
   * @example
   * ```typescript
   * // Update form with latest data from backend
   * @ViewChild(FormViewerComponent) formViewer!: FormViewerComponent;
   *
   * async refreshData() {
   *   const latestData = await this.dataService.getLatestData(this.entityId);
   *   this.formViewer.updateFormData(latestData);
   *   // Form is now updated with latest backend data
   * }
   *
   * // Update form but keep original initialData reference
   * onEdit() {
   *   const editableData = { ...this.currentData, editMode: true };
   *   this.formViewer.updateFormData(editableData, false);
   * }
   * ```
   *
   * @see getFormValue() - Use this to get current form data and validation state
   * @see resetFormValue() - Use this to reset form to original initial state
   * @see markFormAsPristine() - Use this to mark form as saved without losing data
   */
  updateFormData(newData: { [key: string]: any }, updateInitialData: boolean = true): void {
    if (!this._formDefinition) {
      console.warn('Cannot update form data: formDefinition is not set');
      return;
    }

    // Update initialData reference if requested
    if (updateInitialData) {
      this.initialData = { ...newData };
    }

    // Store current form state
    const wasFormDirty = this.form.dirty;

    // Reconstruct form with new data
    this.reconstructFormWithData(newData);

    // If form was not dirty before update, mark as pristine after reconstruction
    if (!wasFormDirty) {
      this.form.markAsPristine();
    }

    // Trigger change detection for field renderers
    if (this.fieldRenderers) {
      this.fieldRenderers.forEach(fieldRenderer => {
        fieldRenderer.refreshValidationState();
      });
    }

    // Manually trigger change detection to ensure UI updates
    this.cdr.markForCheck();
  }

  /**
   * Patch form values without reconstructing form controls
   * Use this for minor updates that don't require form structure changes
   *
   * @param patchData - Data object containing fields to update
   * @param markAsPristine - Whether to mark form as pristine after patching (default: false)
   *
   * @example
   * ```typescript
   * // Update specific fields only
   * @ViewChild(FormViewerComponent) formViewer!: FormViewerComponent;
   *
   * onFieldUpdate() {
   *   this.formViewer.patchFormData({
   *     status: 'updated',
   *     lastModified: new Date()
   *   });
   * }
   *
   * // Update and mark as saved
   * onPartialSave() {
   *   this.formViewer.patchFormData({
   *     draftSaved: true
   *   }, true);
   * }
   * ```
   *
   * @see updateFormData() - Use this for complete form data reconstruction
   * @see getFormValue() - Use this to get current form data and validation state
   */
  patchFormData(patchData: { [key: string]: any }, markAsPristine: boolean = false): void {
    if (!patchData || Object.keys(patchData).length === 0) {
      return;
    }

    // Convert date strings to Date objects for TIME fields
    const processedPatchData = this.processDateFields(patchData);

    // Patch form values
    this.form.patchValue(processedPatchData);

    // Handle repeatable fields separately
    this.patchRepeatableFields(processedPatchData);

    if (markAsPristine) {
      this.form.markAsPristine();
    }

    // Trigger field state updates for dependencies
    this.updateFieldStates();

    // Trigger change detection for field renderers
    if (this.fieldRenderers) {
      this.fieldRenderers.forEach(fieldRenderer => {
        fieldRenderer.refreshValidationState();
      });
    }

    // Manually trigger change detection
    this.cdr.markForCheck();
  }

  /**
   * Private method to reconstruct form with new data
   */
  private reconstructFormWithData(newData: { [key: string]: any }): void {
    // Clear existing form
    this.form = this.fb.group({});
    this.repeatableFieldArrays.clear();
    this.complexFieldGroups.clear();
    // Temporarily set new data as initialData for form reconstruction
    const originalInitialData = this.initialData;
    this.initialData = newData;

    // Set initialization flag
    this.isInitializing = true;

    try {
      // Process all tabs and their sections with new data
      this._formDefinition!.tabs.forEach(tab => {
        tab.sections.forEach(section => {
          this.processSection(section);
        });
      });

      // Extract all fields and update dependency service
      const allFields = this.extractAllFields();
      this.buildFieldDefinitionMap();
      this.formDependencyService.updateFormFields(allFields);

      // Initialize field enabled states
      this.initializeFieldStates(allFields);

      // Re-setup form listeners (they were lost when form was recreated)
      this.setupFormListeners();

    } finally {
      // Restore original initialData if updateInitialData was false
      if (this.initialData === newData && originalInitialData !== newData) {
        // This means updateInitialData was true, keep the new data
      }

      // Initialization complete
      this.isInitializing = false;
    }
  }

  /**
   * Private method to setup form value and status change listeners
   */
  private setupFormListeners(): void {
    // Clear existing subscriptions
    this.destroy$.next();

    // Setup form value change listener with dependency handling
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.updateFieldStates();
        this.valueChange.emit(value);
      });

    // Setup validation listener
    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.validationChange.emit({
          valid: status === 'VALID',
          errors: this.getFormErrors()
        });
      });
  }

  /**
   * Private method to process date fields in patch data
   */
  private processDateFields(data: { [key: string]: any }): { [key: string]: any } {
    const processedData: { [key: string]: any } = {};

    Object.keys(data).forEach(key => {
      const value = data[key];

      // Find field definition to check if it's a date/time field
      const field = this.findFieldByKey(key);
      if (field && (field.type === FieldType.TIME || field['renderType'] === FieldType.TIME)) {
        processedData[key] = this.convertStringToDate(value);
      } else {
        processedData[key] = value;
      }
    });

    return processedData;
  }

  /**
   * Private method to find field definition by key
   */
  private findFieldByKey(fieldKey: string): Field | null {
    if (!this._formDefinition) return null;

    for (const tab of this._formDefinition.tabs) {
      for (const section of tab.sections) {
        for (const row of section.rows) {
          if (!row.isRepeatableRow) {
            for (const columnFields of row.fields) {
              const field = columnFields.find(f => f.key === fieldKey);
              if (field) return field;
            }
          } else if (row.repeatableKeywordField) {
            if (row.repeatableKeywordField.key === fieldKey) {
              return row.repeatableKeywordField;
            }
            const subFields = this.getSubFields(row.repeatableKeywordField);
            const subField = subFields.find(f => f.key === fieldKey);
            if (subField) return subField;
          }
        }
      }
    }

    return null;
  }

  /**
   * Private method to handle patching repeatable fields
   */
  private patchRepeatableFields(patchData: { [key: string]: any }): void {
    Object.keys(patchData).forEach(key => {
      const value = patchData[key];
      if (Array.isArray(value)) {
        const formArray = this.repeatableFieldArrays.get(key);
        if (formArray) {
          // Clear existing instances
          while (formArray.length > 0) {
            formArray.removeAt(0);
          }

          // Find the repeatable field definition
          const field = this.findFieldByKey(key);
          if (field) {
            // Add new instances with data
            value.forEach((instanceData: any, index: number) => {
              const instanceGroup = this.createRepeatableInstanceGroup(
                field,
                instanceData,
                true // Mark as from initial data
              );
              formArray.push(instanceGroup);
            });
          }
        }
      }
    });
  }

  /**
   * Update the section navigator in the right panel
   * Only shows when enableSectionNavigator is true and there are sections
   */
  public updateSectionNavigator(): void {
    if (!this.enableSectionNavigator) {
      this.layoutService.closePanel();
      return;
    }

    const currentSections = this.currentTabSections;

    if (currentSections.length > 0) {
      this.layoutService.openPanel(
        'Sections',
        SectionNavigatorComponent,
        { sections: currentSections }
      );
    } else {
      this.layoutService.closePanel();
    }
  }

  /**
   * Handle tab change event
   */
  onTabChange(event: any): void {
    this.activeTabIndex = event;
    this.updateSectionNavigator();
  }

  /**
   * Get tooltip for selection checkbox
   */
  getSelectionFieldTooltip(repeatableField: Field): string {
    const selectionFieldKey = repeatableField['selectionFieldKey'];
    if (!selectionFieldKey) {
      return '';
    }

    const subFields = repeatableField['subFields'] || [];
    const selectionField = subFields.find((f: any) => f['key'] === selectionFieldKey);

    return selectionField?.['title'] || selectionField?.label || '';
  }

  isDuplicateInstance(fieldKey: string, index: number): boolean {
    return this.duplicateInstances.has(`${fieldKey}:${index}`);
  }

  /** Called by parent after running findDuplicateNewRelatives */
  highlightDuplicates(duplicates: { fieldKey: string; currentIndex: number; existingIndex: number }[]): void {

    const keys = new Set<string>();
    duplicates.forEach(d => {
      keys.add(`${d.fieldKey}:${d.currentIndex}`);
      keys.add(`${d.fieldKey}:${d.existingIndex}`);
    });

    this.duplicateInstances = keys;

    if (duplicates.length > 0) {
      const first = duplicates[0];
      setTimeout(() => {
        document
          .getElementById(`${first.fieldKey}-instance-${first.currentIndex}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  clearDuplicateHighlights(): void {
    this.duplicateInstances.clear();
  }


private findSectionIdForField(fieldKey: string): string | null {

  if (!this.formDefinition?.tabs) {
    return null;
  }

  for (const tab of this.formDefinition.tabs) {
    for (const section of tab.sections ?? []) {
      for (const row of section.rows ?? []) {

        if (row.isRepeatableRow) {
          continue; // street no/name are regular fields, skip repeatable rows
        }

        for (const columnFields of row.fields ?? []) {
          for (const field of columnFields) {
            if (field.key === fieldKey) {
              return section.id;
            }
          }
        }
      }
    }
  }

  return null;
}


isDuplicateAddressSection(sectionId: string): boolean {
  return this.duplicateAddressSections.has(sectionId);
}

highlightDuplicateAddresses(fieldKeys: Set<string>): void {

  const sectionIds = new Set<string>();

  fieldKeys.forEach((fieldKey) => {
    const sectionId = this.findSectionIdForField(fieldKey);
    if (sectionId) {
      sectionIds.add(sectionId);
    } else {
      console.warn(`No section found for field key: ${fieldKey}`); // debug, remove later
    }
  });

  this.duplicateAddressSections = sectionIds;

  const firstSectionId = [...sectionIds][0];

  if (!firstSectionId) {
    return;
  }

  this.cdr.detectChanges();

  setTimeout(() => {
    const el = document.querySelector(`[data-section-id="${firstSectionId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn(`data-section-id="${firstSectionId}" not found in DOM`);
    }
  }, 100);
}

clearDuplicateAddressHighlights(): void {
  this.duplicateAddressSections.clear();
}


}
